import { useState } from "react";
import { api } from "../api";
import { day, num, pct, rupee, signClass } from "../format";
import type { PositionLot } from "../types";

/**
 * What a scaled-in position is actually made of.
 *
 * A blended average is a price at which nothing was bought. Twenty-five shares
 * down 13% and ten shares flat are two different trades with two different
 * reasons to exit, and only this view can tell them apart.
 *
 * Each parcel can carry its own stop. Left blank it uses the position's, which
 * is how most scale-ins are managed; set, it lets the add-on be cut without
 * touching the core.
 */
export function Tranches({
  tradeId,
  lots,
  positionStop,
  onSaved,
}: {
  tradeId: number;
  lots: PositionLot[];
  positionStop: number | null;
  onSaved: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (lots.length === 0) return null;

  const save = async (entryTradeId: string, sl: number | null) => {
    setBusy(entryTradeId);
    setError(null);
    try {
      await api.saveLotJournal(tradeId, entryTradeId, { sl_price: sl });
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className="section-title">
        Tranches · {lots.length}
        {lots.length > 1 && (
          <span className="muted"> — each with its own cost and, if you want, its own stop</span>
        )}
      </div>

      {error && <div className="banner error">{error}</div>}

      <table className="legs">
        <thead>
          <tr>
            <th>Bought</th>
            <th>Qty</th>
            <th>Price</th>
            <th>Unrealized</th>
            <th>Stop</th>
            <th>Initial Risk</th>
            <th>Open Risk</th>
          </tr>
        </thead>
        <tbody>
          {lots.map((lot) => (
            <tr key={lot.entry_trade_id}>
              <td className="nowrap">{day(lot.entry_date)}</td>
              <td>{lot.quantity}</td>
              <td>{num(lot.price)}</td>
              <td className={signClass(lot.unrealized_pnl)}>
                {lot.unrealized_pct === null
                  ? "—"
                  : `${pct(lot.unrealized_pct)} (${rupee(lot.unrealized_pnl, 0)})`}
              </td>
              <td>
                <StopInput
                  value={lot.sl_price}
                  placeholder={positionStop === null ? "—" : `${num(positionStop)} (position)`}
                  disabled={busy === lot.entry_trade_id}
                  onCommit={(v) => save(lot.entry_trade_id, v)}
                />
              </td>
              <td>{lot.initial_risk === null ? "—" : rupee(lot.initial_risk, 0)}</td>
              <td>{lot.open_risk === null ? "—" : rupee(lot.open_risk, 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="hint">
        A parcel with no stop of its own uses the position's. If no stop covers a parcel,
        the position's risk reads as unknown rather than as the total of the parcels that
        do have one — that would understate it.
      </div>
    </>
  );
}

function StopInput({
  value,
  placeholder,
  disabled,
  onCommit,
}: {
  value: number | null;
  placeholder: string;
  disabled: boolean;
  onCommit: (value: number | null) => void;
}) {
  const [draft, setDraft] = useState(value === null ? "" : String(value));

  const commit = () => {
    const trimmed = draft.trim();
    // Emptying the field clears the parcel's own stop rather than storing a
    // zero, which would read as a stop at zero.
    if (trimmed === "") {
      if (value !== null) onCommit(null);
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isNaN(parsed) && parsed !== value) onCommit(parsed);
  };

  return (
    <input
      type="number"
      step="0.05"
      style={{ width: 130 }}
      value={draft}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
    />
  );
}
